import React, { useState, useEffect } from "react";
import {
  FaPhone,
  FaMicrophone,
  FaBolt,
  FaComment,
  FaCheck,
  FaTimes,
  FaBookOpen,
} from "react-icons/fa";
import CircularThumbnail from "./CircularThumbnail";
import personaService from "../../services/personaService";
import PinButton from "../common/PinButton";
import usePins from "../../hooks/usePins";

const PersonaCard = ({
  item,
  onClick,
  height = null,
  compact = true,
  hideFrontPageTopic = false,
  hidePin = false,
}) => {
  const [portraitSrc, setPortraitSrc] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { isPinned, togglePin, canPin } = usePins("personas");

  useEffect(() => {
    const loadPortrait = async () => {
      try {
        if (item.has_image) {
          const url = await personaService.getImageUrl(item.id, item.version);
          setPortraitSrc(url);
          setHasError(false);
        } else {
          setPortraitSrc(null);
          setHasError(false);
        }
      } catch (error) {
        console.error("Error loading portrait for card:", error);
        setHasError(true);
      } finally {
        setHasLoaded(true);
      }
    };

    loadPortrait();
  }, [item.id, item.version, item.has_image]);

  const renderAvatar = () => {
    // Only render the portrait if the item currently has an image AND we have
    // a URL for the current version. `item.has_image` is the source of truth
    // from the latest fetch; a stale `portraitSrc` alone must never render.
    if (compact && item.has_image && portraitSrc && !hasError) {
      return (
        <div className="mr-4">
          <CircularThumbnail imageUrl={portraitSrc} size={64} alt={item.name} />
        </div>
      );
    }

    if (!compact && item.has_image && portraitSrc && !hasError) {
      return (
        <div
          className={`w-full h-[100%] bg-gray-700 rounded-lg overflow-hidden`}
        >
          <img src={portraitSrc} alt={item.name} />
        </div>
      );
    }

    // Fallback to default avatar
    return (
      <img
        src="/default-avatar.png"
        alt={item.name}
        className="w-16 h-16 rounded-full object-cover mr-4"
      />
    );
  };

  return (
    <div
      className={`card-glass relative ${height ? "" : "h-full"}`}
      style={height ? { height } : {}}
      onClick={onClick}
    >
      {compact && item.external_voice_bot_id && (
        <div
          className="absolute top-4 right-4 bg-gradient-to-r from-green-600/30 to-green-500/30 border border-green-500/40 text-green-200 text-[10px] font-medium px-2 py-0.5 rounded-full max-w-[120px] truncate"
          title={item.external_voice_bot_id}
        >
          {item.external_voice_bot_id}
        </div>
      )}

      <div className="flex flex-col h-full">
        <div
          className={`flex items-center mb-4 ${
            !compact ? "min-h-[164px]" : ""
          }`}
        >
          {hasLoaded && renderAvatar()}
          {compact && (
            <h3
              className={`text-xl font-semibold text-gray-100 ${item.external_voice_bot_id ? "pr-24" : ""}`}
            >
              {item.name} (v{item.version})
            </h3>
          )}
        </div>
        {!compact && (
          <div className="flex items-center mb-2">
            <h3 className="text-xl font-semibold text-gray-100">
              {item.name} (version {item.version})
            </h3>
          </div>
        )}
        <div className="relative group flex-grow">
          <p
            className={`text-gray-400 mb-4 line-clamp-3 overflow-hidden ${
              compact ? "text-xs" : "text-sm"
            }`}
          >
            {item?.description}
          </p>
          <div className={`hint-glass ${compact ? "text-xs" : "text-sm"}`}>
            {item?.description}
          </div>
        </div>
        <div className="mt-auto space-y-3">
          <div className="flex items-center justify-between text-gray-300">
            <div className="flex items-center">
              <FaPhone className="mr-2" />
              <span className="text-sm text-gray-100">
                {item.channel?.phone_number}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                <FaMicrophone className="mr-1" />
                {item.channel?.voice_supported ? (
                  <FaCheck className="text-green-500" />
                ) : (
                  <FaTimes className="text-red-500" />
                )}
              </div>
              <div className="flex items-center">
                <FaComment className="mr-1" />
                {item.channel?.sms_supported ? (
                  <FaCheck className="text-green-500" />
                ) : (
                  <FaTimes className="text-red-500" />
                )}
              </div>
              <div className="flex items-center">
                <FaBolt className="mr-1" />
                {item.enable_pt ? (
                  <FaCheck className="text-green-500" />
                ) : (
                  <FaTimes className="text-red-500" />
                )}
              </div>
            </div>
          </div>

          {!compact && (item.external_voice_bot_id || item.models) && (
            <div className="py-2">
              <div className="flex space-x-4 items-center justify-start">
                {item.external_voice_bot_id ? (
                  <div className="inline-flex shrink-0 items-center p-1 rounded-sm bg-gradient-to-r from-green-600/20 to-green-500/30 border border-green-500/30">
                    <div className="flex flex-col min-h-[32px] justify-center px-2">
                      <span
                        className="text-xs font-semibold text-green-100 text-left line-clamp-2"
                        title={item.external_voice_bot_id}
                      >
                        Voice Bot: {item.external_voice_bot_id}
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    {(item.models.voice?.stt || item.models.stt) && (
                      <div className="inline-flex shrink-0 items-start p-1 rounded-sm bg-gradient-to-r from-blue-600/20 to-blue-500/30 border border-blue-500/30">
                        <div className="flex flex-col w-[84px] min-h-[32px]">
                          <span
                            className="text-xs font-medium text-blue-100 text-center line-clamp-2"
                            title={item.models.voice?.stt || item.models.stt}
                          >
                            {item.models.voice?.stt || item.models.stt}
                          </span>
                        </div>
                      </div>
                    )}

                    {(item.models.voice?.generative ||
                      item.models.sms?.generative ||
                      item.models.generative) && (
                      <div className="inline-flex shrink-0 items-center p-1 rounded-sm bg-gradient-to-r from-purple-600/20 to-purple-500/30 border border-purple-500/30">
                        <div className="flex flex-col w-[84px] min-h-[32px]">
                          <span
                            className="text-xs font-medium text-purple-100 text-center line-clamp-2"
                            title={
                              item.models.voice?.generative ||
                              item.models.sms?.generative ||
                              item.models.generative
                            }
                          >
                            {item.models.voice?.generative ||
                              item.models.sms?.generative ||
                              item.models.generative}
                          </span>
                        </div>
                      </div>
                    )}

                    {(item.models.voice?.tts || item.models.tts) && (
                      <div className="inline-flex shrink-0 items-end p-1 rounded-sm bg-gradient-to-r from-green-600/20 to-green-500/30 border border-green-500/30">
                        <div className="flex flex-col w-[84px] min-h-[32px]">
                          <span
                            className="text-xs font-medium text-green-100 text-center line-clamp-2"
                            title={item.models.voice?.tts || item.models.tts}
                          >
                            {item.models.voice?.tts || item.models.tts}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {item.external_voice_gateway_id && (
            <div className="inline-flex shrink-0 items-center p-1 rounded-sm bg-gradient-to-r from-yellow-600/20 to-yellow-500/30 border border-yellow-500/30">
              <div className="flex flex-col min-h-[32px] justify-center px-2">
                <span
                  className="text-xs font-semibold text-yellow-100 text-left line-clamp-2"
                  title={item.external_voice_gateway_id}
                >
                  Voice Gateway: {item.external_voice_gateway_id}
                </span>
              </div>
            </div>
          )}

          {!compact && !hideFrontPageTopic && (
            <div className="min-h-10">
              {item.metadata?.chat_ui_topics?.length > 0 && (
                <div className="bg-gray-700 rounded-lg p-2 shadow-md">
                  <div className="flex justify-between items-center">
                    <div className="flex text-gray-100">
                      <FaBookOpen className="mr-2 min-w-3 h-full text-blue-400 self-center" />
                      <div className="overflow-hidden text-xs pt-1">
                        {item.metadata.chat_ui_topics.map((topic, index) => (
                          <span key={index}>
                            {topic.name}
                            <span className="bg-blue-500 text-white px-1 py-[2px] rounded-md ml-1">
                              v{topic.version}
                            </span>
                            <div className="min-h-[3px]" />
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div
            className={`flex items-start justify-between gap-2 min-h-${
              compact ? "8" : "[60px]"
            }`}
          >
            <div className="flex flex-wrap items-start">
              {item.tags?.map((tag) => (
                <span
                  key={tag}
                  className={`bg-blue-600 text-white ${
                    compact ? "text-xs px-1.5" : "text-xs px-2"
                  } py-1 rounded-full mr-2 mb-1.5`}
                >
                  {tag}{" "}
                </span>
              ))}
            </div>
            {!hidePin && !item?.deleted_at && (
              <PinButton
                className="flex-shrink-0"
                size={18}
                pinned={isPinned(item?.id)}
                disabled={!canPin}
                onToggle={() => togglePin(item?.id)}
              />
            )}
          </div>

          {item?.deleted_at && (
            <div className="mt-2 text-red-500 text-sm font-semibold">
              Deleted {new Date(item.deleted_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonaCard;
